import { map, share } from 'rxjs/operators';

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { InspectionPoint, Inspection } from '../models/index';
import * as _ from 'lodash';
import { ApiService } from './api.service';
import { DataService } from './data.service';
import { PluginComponent } from '../../plugins/plugin.component';
import { BaseService } from './baseService';
import { InspectionFactoryService } from './inspectionFactory.service';
import { CommentPluginService } from '../../plugins/comment/comment-plugin.service';
import { OfflineApiService } from './offlineApi.service';
import { ApiMode } from '../../utils/enums';
import { AppConstants } from '../../utils/appConstants.class';
import { SnackBarService } from './snack-bar.service';
import { InspectionFolderService } from './folder.service';
import { PartsPluginService } from '../../plugins/parts/parts-plugin.service';

@Injectable({
  providedIn: 'root'
})
export class InspectionPointService extends BaseService {
  points: InspectionPoint[] = [];
  public point: InspectionPoint;
  private baseUrl = '/inspections/';

  public notApplicableValues: any = [
    { value: 0, label: 'Added by mistake' },
    { value: 1, label: 'No time' },
    { value: 2, label: 'Not applicable' },
    { value: 3, label: 'Client asked not to inspect' },
    { value: 4, label: 'Other reasons' }
  ];

  constructor(
    private apiService: ApiService,
    private dataService: DataService,
    private offlineApi: OfflineApiService,
    private factory: InspectionFactoryService,
    private snackBar: SnackBarService,
    private commentPluginService: CommentPluginService,
    private folderService: InspectionFolderService,
    private partsService: PartsPluginService
  ) {
    super();
  }

  public setBaseUrl(newBaseUrl: string) {
    this.baseUrl = newBaseUrl;
  }

  public getNotApplicableValues() {
    return this.notApplicableValues;
  }

  getValue(value: any, array: any): string {
    const obj = _.find(array, ['value', value]);
    return obj ? obj.label : '';
  }

  public getPoint(id: string, pointId: string): Observable<InspectionPoint> {
    return this.dataService.getPoint(id, pointId);
  }

  public getPoints(inspectionId: string, sectionId: string): Observable<InspectionPoint[]> {
    return this.dataService.getSectionPoints(inspectionId, sectionId);
  }

  public setPoint(inspectionPoint: InspectionPoint): void {
    this.point = inspectionPoint;
  }

  public updateStatus(id: string, pointId: string, status: number): Observable<InspectionPoint> {
    const url = `${this.baseUrl}${id}/points/${pointId}/status/${status}`;
    const inspection = this.dataService.getInspection(id);
    return this._updateStatus(id, pointId, status).pipe(
      share(),
      map((res: any) => {
        const point = <InspectionPoint>res.point;
        const p = inspection.pointsById[point.id];
        p.status = point.status;
        inspection.progression = res.inspectionProgression;
        if (status === InspectionPoint.Done) {
          this.dataService.setChangedPoint(pointId, false);
        }
        this.initPointPlugins(p);
        return p;
      })
    );
  }

  public updatePointsStatus(id: string, pointIds: string[], status: number): Observable<InspectionPoint[]> {
    const inspection = this.dataService.getInspection(id);
    return this._updatePointsStatus(id, pointIds, status).pipe(
      share(),
      map((res: any) => {
        const points = <InspectionPoint[]>res.points;
        points.forEach((point) => {
          const p = inspection.pointsById[point.id];
          p.status = point.status;
          inspection.progression = res.inspectionProgression;
          if (status === InspectionPoint.Done) {
            this.dataService.setChangedPoint(point.id, false);
          }
          this.initPointPlugins(p);
        });
        return points;
      })
    );
  }

  public restorePoint(id: string, pointId: string): Observable<InspectionPoint> {
    const inspection = this.dataService.getInspection(id);
    return this._restorePoint(id, pointId).pipe(
      share(),
      map((res: any) => {
        const point = <InspectionPoint>res.point;
        const p = inspection.pointsById[point.id];
        p.status = point.status;
        p.reason = point.reason;
        p.comment = point.comment;
        inspection.progression = res.inspectionProgression;
        return p;
      })
    );
  }

  public updatePointProperties(inspectionId: string, pointId: string, params: any): Observable<InspectionPoint> {
    return this._updatePointProperties(inspectionId, pointId, params).pipe(
      map((point: InspectionPoint) => {
        return point;
      })
    );
  }

  public updatePointNotes(inspectionId: string, pointId: string, params: any): Observable<InspectionPoint> {
    return this._updatePointNotes(inspectionId, pointId, params).pipe(
      map((point: InspectionPoint) => {
        return point;
      })
    );
  }

  public createPoint(id: string, sectionId: string, model: any): Observable<InspectionPoint> {
    return this._createPoint(id, sectionId, null, model).pipe(
      map((point: InspectionPoint) => {
        return this.dataService.updatePoint(id, point);
      })
    );
  }

  public createPointInFolder(id: string, sectionId: string, folderId: string, model: any) {
    return this._createPoint(id, sectionId, folderId, model).pipe(
      map((point: InspectionPoint) => {
        return this.dataService.updatePoint(id, point);
      })
    );
  }

  public setNotApplicable(inspectionId: string, pointId: string, reason: string, comment: string): Observable<InspectionPoint> {
    const inspection = this.dataService.getInspection(inspectionId);
    return this._setNotApplicable(inspectionId, pointId, reason, comment).pipe(
      map((res: any) => {
        const point = <InspectionPoint>res.point;
        const p = inspection.pointsById[point.id];
        p.status = point.status;
        p.reason = point.reason;
        p.comment = point.comment;
        inspection.progression = res.inspectionProgression;
        return p;
      })
    );
  }

  public moveToSection(id: string, sectionId: string, folderId: string = null, model): Observable<Inspection> {
    return this._moveToSection(id, sectionId, folderId, model).pipe(
      share(),
      map((response: Inspection) => {
        return this.dataService.processInspection(response);
      }),
      switchMap((updatedInspection) => {
        if (model.deleteSource && this.isInspectionInOffline(model.sourceInspectionId) && model.selectedItems.some((s) => s.isFolder)) {
          return this.folderService
            .deleteFolders(
              model.sourceInspectionId,
              model.sourceSectionId,
              model.selectedItems.filter((s) => s.isFolder).map((p) => p.id)
            )
            .map(() => updatedInspection);
        }
        return of(updatedInspection);
      }),
      switchMap((updatedInspection) => {
        if (model.deleteSource && this.isInspectionInOffline(model.sourceInspectionId) && model.selectedItems.some((s) => !s.isFolder)) {
          return this.deletePoints(
            model.sourceInspectionId,
            model.selectedItems.filter((s) => !s.isFolder).map((p) => p.id)
          ).map(() => updatedInspection);
        }
        return of(updatedInspection);
      }),
      map((response: Inspection) => {
        if (model.deleteSource) {
          this.dataService.removeParts(
            model.sourceInspectionId,
            model.selectedItems.filter((s) => !s.isFolder).map((f) => f.id)
          );
        }
        return response;
      })
    );
  }

  public orderPlugins(id: string, pointId: string, orderableItems: any) {
    return this._orderPlugins(id, pointId, orderableItems).pipe(
      map((point: InspectionPoint) => {
        this.dataService.updatePointPluginsOrder(id, pointId, orderableItems);
      })
    );
  }

  public deletePoints(id: string, pointIds: Array<string>): Observable<any> {
    return this._deletePoints(id, pointIds).pipe(
      map((res: any) => {
        if (res.deletedPoints) {
          res.deletedPoints.forEach((pointId) => this.dataService.removePoint(id, pointId));
          this.partsService.updateParts(id, '', '', [], null).subscribe();
        }
        const inspection = this.dataService.getInspection(id);
        inspection.progression = res.inspectionProgression;
        this.dataService.removeParts(id, pointIds);
        return res.deletedPoints;
      })
    );
  }

  public isPointChanged(pointId: string) {
    return this.dataService.isPointChanged(pointId);
  }

  public cancelChanges(pointId: string) {
    this.dataService.setChangedPoint(pointId, false);
  }

  public initPointPlugins(point: InspectionPoint) {
    for (let i = 0; i < point.plugins.length; i++) {
      let plugin = point.plugins[i];
      if (PluginComponent.initializers[plugin.pluginType]) {
        PluginComponent.initializers[plugin.pluginType](plugin, this.commentPluginService);
      }
    }
  }

  private _updateStatus(id: string, pointId: string, status: number): Observable<any> {
    const url = `${this.baseUrl}${id}/points/${pointId}/status/${status}`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.updatePointStatus(id, pointId, status);
    } else {
      return this.apiService.patch(url, null);
    }
  }

  private _updatePointsStatus(id: string, pointIds: string[], status: number): Observable<any> {
    const url = `/inspections/${id}/points/status/${status}`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.updatePointsStatus(id, pointIds, status);
    } else {
      return this.apiService.post(url, pointIds);
    }
  }

  private _updatePointNotes(inspectionId: string, pointId: string, params: any): Observable<InspectionPoint> {
    const url = `${this.baseUrl}${inspectionId}/points/${pointId}/editnotes`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.updatePointNotes(inspectionId, pointId, params);
    }
    return this.apiService.patch(url, params);
  }

  private _setNotApplicable(inspectionId: string, pointId: string, reason: string, comment: string): Observable<InspectionPoint> {
    const url = `${this.baseUrl}${inspectionId}/points/${pointId}/setnotapplicable`;
    const data = { reason, comment };
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.setNotApplicable(inspectionId, pointId, data);
    } else {
      return this.apiService.patch(url, data);
    }
  }

  private _restorePoint(inspectionId: string, pointId: string): Observable<InspectionPoint> {
    const url = `/inspections/${inspectionId}/points/${pointId}/restore`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.setNotApplicable(inspectionId, pointId, { reason: '', comment: '' });
    } else {
      return this.apiService.patch(url, null);
    }
  }

  private _orderPlugins(inspectionId: string, pointId: string, orderableItems: any) {
    const url = `${this.baseUrl}${inspectionId}/points/${pointId}/plugins/order`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.orderPlugins(inspectionId, pointId, orderableItems);
    } else {
      return this.apiService.post(url, { OrderableItems: orderableItems });
    }
  }

  private _updatePointProperties(inspectionId: string, pointId: string, params: any): Observable<InspectionPoint> {
    const url = `${this.baseUrl}${inspectionId}/points/${pointId}`;
    if (this.isInspectionInOffline(inspectionId)) {
      return this.factory.updatePointProperties(inspectionId, pointId, params);
    } else {
      return this.apiService.patch(url, params);
    }
  }

  private _deletePoints(id: string, pointIds: Array<string>): Observable<any> {
    const url = `${this.baseUrl}${id}/points/delete`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.deletePoints(id, pointIds);
    }
    return this.apiService.post(url, pointIds);
  }

  private _createPoint(id: string, sectionId: string, folderId: string, model: any): Observable<InspectionPoint> {
    const url = folderId
      ? `${this.baseUrl}${id}/sections/${sectionId}/folders/${folderId}/points/new`
      : `${this.baseUrl}${id}/sections/${sectionId}/points/new`;
    if (this.isInspectionInOffline(id)) {
      return this.factory.createPoint(id, sectionId, folderId, model);
    }
    return this.apiService.post(url, model);
  }

  private _getInspection(id: string, forceCheckUpdates = false): Observable<Inspection> {
    const url = `/inspections/${id}`;
    if (this.isManager) {
      return this.apiService.get(url);
    }
    return this.offlineApi.getSyncUpdates(url, 'inspections', id, forceCheckUpdates);
  }

  private _moveToSection(id: string, sectionId: string, folderId: string = null, model): Observable<Inspection> {
    const url = folderId
      ? `${this.baseUrl}${id}/points/sections/${sectionId}/folder/${folderId}`
      : `${this.baseUrl}${id}/points/section/${sectionId}`;

    const data = {
      sectionItems: folderId ? model.selectedItems.filter((s) => !s.isFolder) : model.selectedItems,
      sourceInspectionId: model.sourceInspectionId,
      sourceSectionId: model.sourceSectionId,
      deleteSource: model.deleteSource
    };
    if (this.isInspectionInOffline(id)) {
      const sourceInspectionUrl = `/inspections/${model.sourceInspectionId}`;
      const source$ = this.isInspectionInOffline(model.sourceInspectionId)
        ? this.offlineApi.getWithMode(
            sourceInspectionUrl,
            ApiMode.Offline,
            AppConstants.INDEXEDDB_TABLES.inspections,
            model.sourceInspectionId
          )
        : this._getInspection(model.sourceInspectionId);
      return this.factory.copyToSection(id, sectionId, folderId, source$, data);
    } else if (this.isInspectionInOffline(model.sourceInspectionId)) {
      this.snackBar.warn('snack-bar.something-went-wrong');
      return of(null);
    }
    return this.apiService
      .post(url, data)
      .pipe(switchMap((updatedInspection) => this.factory.addInspection(updatedInspection).pipe(map(() => updatedInspection))));
  }

  public getPointEntity(inspectionId: string, pointId: string): Observable<any> {
    const url = `${this.baseUrl}${inspectionId}/points/${pointId}/entity`;
    return this.apiService.get(url);
  }

  public updatePointEntity(inspectionId: string, pointId: string, point: any): Observable<any> {
    const url = `${this.baseUrl}${inspectionId}/points/${pointId}/entity`;
    return this.apiService.put(url, point);
  }

  public getPointFromServer(inspectionId: string, pointId: string, headers?: { [header: string]: string | string[] }): Observable<any> {
    const url = `${this.baseUrl}${inspectionId}/points/${pointId}`;
    return this.apiService.get(url, headers);
  }
}
